import {Fragment} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {
  getLineItemCoverDisplayParts,
  resolveLineItemOptionDisplayPartsFromPayload,
} from '@amblique/king-living/app/utils/cart-utils';

const CLASS_NAMES_BY_VARIANT = {
  cartItem: {
    label: 'cart-item__config-label',
    row: 'cart-item__config-text',
    value: 'cart-item__config-value',
  },
  confirmationLineItem: {
    label: 'confirmation-line-item__config-label',
    row: 'confirmation-line-item__config-text',
    value: 'confirmation-line-item__config-value',
  },
};

const defaultGetOptionKey = (opt, idx) => {
  const parts = [opt.containerId, opt.name, opt.productId, idx].filter(
    (part) => part != null && part !== ''
  );
  return parts.length ? parts.join('-') : `option-${idx}`;
};

/**
 * @param {{ coverMaterialName?: string|null, coverProductName?: string|null, coverName?: string|null }} cover
 * @returns {import('react').ReactNode|null}
 */
const renderCoverConfigValue = (cover) => {
  const {materialName, productName} = getLineItemCoverDisplayParts(cover);

  if (!productName && !materialName) return null;

  if (materialName && productName) {
    return (
      <Fragment>
        <span className="line-item-config__accent">{materialName}</span>
        {' '}
        {productName}
      </Fragment>
    );
  }

  return productName || materialName;
};

/**
 * @param {{ productName?: string|null, shortDescription?: string|null, value?: string|null }} option
 * @returns {import('react').ReactNode}
 */
const renderOptionConfigValue = (option) => {
  const {fallbackValue, productName, shortDescription} = resolveLineItemOptionDisplayPartsFromPayload(option);
  const hasStructuredParts = Boolean(productName || shortDescription);

  if (!hasStructuredParts) {
    return fallbackValue;
  }

  if (productName && shortDescription) {
    return (
      <Fragment>
        {productName}
        {' '}
        <span className="line-item-config__accent">{shortDescription}</span>
      </Fragment>
    );
  }

  return productName || shortDescription || fallbackValue;
};

const LineItemConfigRow = ({classNames, label, rowStyles, value}) => (
  <Text className={classNames.row} {...rowStyles}>
    <span className={classNames.label}>{label}</span>
    {': '}
    <span className={classNames.value}>{value}</span>
  </Text>
);

LineItemConfigRow.displayName = 'LineItemConfigRow';
LineItemConfigRow.propTypes = {
  classNames: PropTypes.shape({
    label: PropTypes.string.isRequired,
    row: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  label: PropTypes.node.isRequired,
  rowStyles: PropTypes.object,
  value: PropTypes.node.isRequired,
};

/**
 * Read-only configuration summary for a basket or order line item on a master product:
 * cover variant (when present) and visible configured option SKUs as `label: value` rows.
 *
 * @param {Object} [props.coverConfig] - Cover block from `selectLineItemConfigDisplayFromPayload`
 * @param {Object[]} [props.options] - Option rows; entries with `isHidden` are not rendered
 * @param {'cartItem'|'confirmationLineItem'} [props.variant] - Cart flyout/checkout vs confirmation styling
 */
const LineItemConfiguration = ({
  coverConfig,
  getOptionKey = defaultGetOptionKey,
  options = [],
  variant = 'cartItem',
}) => {
  const styles = useMultiStyleConfig('LineItemConfiguration', {variant});
  const classNames = CLASS_NAMES_BY_VARIANT[variant];
  const coverValue = coverConfig ? renderCoverConfigValue(coverConfig) : null;
  const visibleOptions = (options ?? []).filter((opt) => opt && !opt.isHidden);

  return (
    <>
      {coverValue != null && coverValue !== '' && (
        <LineItemConfigRow
          classNames={classNames}
          rowStyles={styles.row}
          value={coverValue}
          label={
            <FormattedMessage defaultMessage="Cover" id="line_item_config.cover_label" />
          }
        />
      )}
      {visibleOptions.map((opt, idx) => (
        <LineItemConfigRow
          key={getOptionKey(opt, idx)}
          classNames={classNames}
          label={opt.name}
          rowStyles={styles.row}
          value={renderOptionConfigValue(opt)}
        />
      ))}
    </>
  );
};

LineItemConfiguration.displayName = 'LineItemConfiguration';
LineItemConfiguration.propTypes = {
  coverConfig: PropTypes.object,
  getOptionKey: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.object),
  variant: PropTypes.oneOf(['cartItem', 'confirmationLineItem']),
};

export default LineItemConfiguration;
